import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import defaultTexts from '../../../texts/texts'
import constants from '../../../config/constants'
import useWindowDimensions from '../../_common/WindowDimesnions'
import { request } from '../../../_network/request'
import {
  clientAccountsURLs,
  subscriptionsURLs,
  serviceConfigsURLs,
  billingAccountConfigsURLs,
  billingAccountServicesURLs,
  fileManagerURLs,
} from '../../../_network/apiUrls'
import { setAccountBalance } from '../../../store/reducers/profileSlice'
import { setProgressStep, setPendingPayment, subscriptionAddReset } from '../../../store/reducers/subscriptionAddSlice'
import { getFormattedDateTime, calculateDateTime, getIsoDateTime } from '../_helpers/dates'
import { isWildcardMatch } from '../_helpers/rsaCertificates'
import { getAllowedRoles } from '../_helpers/billing'
import parse from 'html-react-parser'
import CustomTextField from '../../_common/_form_fields/CustomTextField'
import CustomDialog from '../../_common/CustomDialog'
import CustomBackdrop from '../../_common/CustomBackdrop'
import SubmitPaymentMethodBalance from './SubmitPaymentMethodBalance'
// import SubmitPaymentMethodSubscribe from './SubmitPaymentMethodSubscribe'
import PaypalWrapper from '../../_common/paypal/PaypalWrapper'
import CustomSlickSlider from '../../_common/CustomSlickSlider'

const paypalOptions = {
  'client-id': constants.paypalClientId,
  currency: constants.paypalCurrency,
  intent: 'capture',
}

const SubmitWrapper = (props) => {
  const {
    setStep,
    totalFee,
    subscriptionPackagesData,
    selectedSubscription,
    supportedPaymentMethods,
    supportedServiceConfigs,
    appCredentialSecret,
    paymentMethodIds,
  } = props
  const theme = useTheme()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { width } = useWindowDimensions()

  const account_id = useSelector(state => state.profile.account_id)
  const account_balance = useSelector(state => state.profile.account_balance)
  const selected_services = useSelector(state => state.subscriptionAdd.subscription_package.services)
  const openstack_configs = useSelector(state => state.subscriptionAdd.openstack_configs)
  const admin_ui_configs = useSelector(state => state.subscriptionAdd.admin_ui_configs)
  const customer_ui_configs = useSelector(state => state.subscriptionAdd.customer_ui_configs)
  const billing_configs = useSelector(state => state.subscriptionAdd.billing_configs)
  const billing_permissions = useSelector(state => state.subscriptionAdd.billing_permissions)
  const subscription_renewable = useSelector(state => state.subscriptionAdd.subscription_renewable)
  const pending_payment = useSelector(state => state.subscriptionAdd.pending_payment)
  const group = useSelector(state => state.subscriptionAdd.group)
  
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null)
  const [amount, setAmount] = useState('')
  const [allowSubmit, setAllowSubmit] = useState(false)
  const [payResultDialogOpen, setPayResultDialogOpen] = useState(false)
  const [payResultText, setPayResultTexs] = useState('')
  const [refillBalanceData, setRefillBalanceData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const [paymentMethodsSliderData, setPaymentMethodsSliderData] = useState([])
  const [paymentMethodsSliderSlidesToShow, setPaymentMethodsSliderSlidesToShow] = useState(0)

  const labelStyle = {
    color: theme.palette.folly,
    fontSize: '20px',
    fontWeight: '500',
    padding: '40px 0px 20px 0px',
  }

  const paymentMethodsWrapperStyle = {
    borderTop: `${theme.palette.lightSilver} 1px dotted`,
    borderBottom: `${theme.palette.lightSilver} 1px dotted`,
    padding: '20px',
    margin: '0px -20px',
  }

  const formControlStyle = {
    background: 'white',
    color: theme.palette.grayX11,
    borderRadius: '10px',
    width: '100%',
  }

  const outlinedInputStyle = {
    '& .MuiInputBase-input::label': {
      color: theme.palette.grayX11,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.oxfordBlue,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.oxfordBlue,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.oxfordBlue,
    },
  }

  const currencyLabelStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: '18px',
    fontWeight: '500',
    paddingTop: '15px',
    textTransform: 'uppercase',
  }

  // const creditCardButtonStyle = {
  //   textTransform: 'capitalize',
  //   fontWeight: '500',
  //   color: 'white',
  //   background: theme.palette.pictonBlue,
  //   fontSize: '18px',
  //   width: '100%',
  //   height: '50px',
  //   borderRadius: '25px',
  //   padding: '0px 10%',
  //   marginBottom: '20px',

  //   '&:hover': {
  //     background: alpha(theme.palette.pictonBlue, 0.8),
  //   },
  // }

  const subscribeHelperTextStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: '18px',
    fontWeight: '500',
  }

  const submitButtonStyle = {
    fontWeight: '400',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '18px',
    height: '44px',
    borderRadius: '22px',
    padding: '0px 40px',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },

    '&:disabled': {
      background: theme.palette.grayX11,
      color: 'white',
    },
  }

  const nextButtonStyle = {
    fontWeight: '400',
    color: 'white',
    background: theme.palette.violetsAreBlue,
    fontSize: '18px',
    height: '44px',
    borderRadius: '22px',
    padding: '0px 40px',

    '&:hover': {
      background: alpha(theme.palette.violetsAreBlue, 0.8),
    },

    '&:disabled': {
      background: theme.palette.grayX11,
      color: 'white',
    },
  }

  const paymentMethodsSliderArrows = {
    leftIcon: <ArrowBackIcon fontSize='large' />,
    rightIcon: <ArrowForwardIcon fontSize='large' />,
    leftStyle: {
      width: '50px',
      height: '50px',
      border: `${theme.palette.oxfordBlue} 2px solid`,
      borderRadius: '25px',
      color: theme.palette.oxfordBlue,
      position: 'absolute',
      top: '40%',
      left: '-50px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },    
    rightStyle: {
      width: '50px',
      height: '50px',
      border: `${theme.palette.oxfordBlue} 2px solid`,
      borderRadius: '25px',
      color: theme.palette.oxfordBlue,
      position: 'absolute',
      top: '40%',
      right: '-50px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  }
  
  const getCurrencyCode = (currency) => {
    let currency_code = ''

    supportedPaymentMethods.forEach(pm => {
      if (pm.currencies && pm.currencies.length > 0) {
        pm.currencies.forEach(cu => {
          if (currency.toUpperCase() === cu.name.toUpperCase()) {
            currency_code = cu.code
          }
        })
      }
    })

    return currency_code
  }

  const getSubscription = () => {
    const subscription = subscriptionPackagesData.find(item => item.id === selectedSubscription)
    return subscription ? subscription : null
  }

  const getServiceTitle = (service, subscription) => {
    const service_object = subscription.services.find(item => item.service === service)
    return service_object ? service_object.title : ''
  }

  const getDomainList = (service_domain) => {
    let result = []

    service_domain.forEach(item => {
      result.push(item.subdomain_prefix ? item.subdomain_prefix + '.' + item.domain_name : item.domain_name)
    })

    return result
  }

  const getDomainListFromCertificate = (service_domain, certificate_domain) => {
    let result = []

    service_domain.forEach(d => {
      const  domain_name = d.subdomain_prefix ? d.subdomain_prefix + '.' + d.domain_name : d.domain_name

      const is_wildcard_match = isWildcardMatch(certificate_domain, domain_name)
      if (is_wildcard_match && !result.includes(domain_name)) {
        result.push(domain_name)
      }
    })

    return result
  }

  const getDocumentFile = (label, documents) => {
    const document = documents.find(item => item.label === label)
    if (document && document.type && document.type === 'file' && typeof document.value !== 'string') {
      return document.value
    }
  }

  const removeSpacesAndSpecialChars = (str) => {
    return str.replace(/[\s~`!@#$%^&*(){}[\];:"'<,>?/\\|+=-]/g, '')
  }

  const handlePayResultDialogOpen = (text) => {
    setPayResultDialogOpen(true)
    setPayResultTexs(text)
  }

  const handlePayResultDialogClose = () => {
    setPayResultDialogOpen(false)
    setPayResultTexs('')
  }

  const handleSelectPaymentMethod = (value) => {
    setSelectedPaymentMethod(prev => prev === value ? null : value)
  }

  const handleChangeAmount = (event) => {
    const value = event.target.value
    if (value && value.match(/^[0-9]*\.?[0-9]*$/)) {
      setAmount(parseFloat(value))
    }
  }

  const handleClickPrev = () => {
    let prev_step = 3

    if (group === 'isender') {
      prev_step = 2
    }

    setStep(prev_step)
    dispatch(setProgressStep({ progress_step: prev_step }))
  }

  const onSuccessfulPayment = (paymentDetails) => {
    handlePayResultDialogOpen(defaultTexts.paymentSuccessfullText)
    
    const payment_amount = paymentDetails.purchase_units[0].amount.value
    const payment_currency = paymentDetails.purchase_units[0].amount.currency_code
    const payment_currency_code = getCurrencyCode(paymentDetails.purchase_units[0].amount.currency_code)
    
    const postData = {
      client_id: account_id,
      amount: payment_amount,
      currency: payment_currency,
      currency_code: payment_currency_code,
      payment_method: 'paypal',
      transaction_data: paymentDetails,
    }
    
    setRefillBalanceData(postData)
    setAmount('')

    if (payment_amount + account_balance >= totalFee) {
      dispatch(setPendingPayment({ pending_payment: true }))
    }
  }

  const onFailedPayment = () => {
    handlePayResultDialogOpen(defaultTexts.paymentFailedText)
    setAmount('')
  }

  const uploadFileRequest = async (file_to_upload, url_key) => {
    const url = constants.core_service_domain + '/' + fileManagerURLs[url_key]

    const form_data = new FormData()
    form_data.append('file', file_to_upload, removeSpacesAndSpecialChars(file_to_upload.name))

    const request_data = { url, method: 'POST', form_data }

    const response = await request(request_data)

    if (response && response.status_code && response.status_code < 400 && response.data && response.data.url) {
      return response.data.url
    }
  }

  const clientSubscriptionRequest = async (postData) => {
    const url = constants.core_service_domain + '/' + subscriptionsURLs.getClientSubscriptions
    const request_data = { url, method: 'POST', data: postData }
    
    const response = await request(request_data)
    
    if (response && response.status_code && response.status_code < 400) {
      return true
    } else {
      return false
    }
    // console.log('client_subscription', request_data)
  }

  const clientServiceConfigsRequest = async (postData) => {
    const url = constants.core_service_domain + '/' + serviceConfigsURLs.getClientServiceConfigs
    const request_data = { url, method: 'POST', data: postData }
    await request(request_data)
    // console.log('client-service-configs', request_data)
  }

  const clientCertificateConfigsRequest = async (postData) => {
    const url = constants.core_service_domain + '/' + serviceConfigsURLs.getClientCertificateConfigs
    const request_data = { url, method: 'POST', data: postData }
    await request(request_data)
  }

  const billingAccountConfigsRequest = async (postData) => {
    const url = constants.billing_url + '/' + billingAccountConfigsURLs.getAccountConfigs
    const request_data = { url, method: 'POST', data: postData, headers: { 'customer-account-id': account_id } }
    await request(request_data)
    // console.log('billing-account-configs', request_data)
  }

  const billingAccountConfigsUpdateRequest = async (postData) => {
    const url = constants.billing_url + '/' + billingAccountConfigsURLs.getAccountConfigs
    const request_data = { url, method: 'PUT', data: postData, headers: { 'customer-account-id': account_id } }
    await request(request_data)
    // console.log('billing-account-update-configs', request_data, response)
  }

  const billingAccountConfigsIdRequest = async () => {
    const url = constants.billing_url + '/' + billingAccountConfigsURLs.getAccountConfigs
    const request_data = { url, method: 'GET', headers: { 'customer-account-id': account_id } }
    const response = await request(request_data)
    if (response.status_code && response.status_code < 400 && response.data && response.data.id) {
      return response.data.id
    }
    return null
  }

  const billingAccountServicesRequest = async (postData) => {
    const url = constants.billing_url + '/' + billingAccountServicesURLs.getAccountServices
    const request_data = { url, method: 'POST', data: postData, headers: { 'customer-account-id': account_id } }
    await request(request_data)
    // console.log('billing-account-services', request_data)
  }

  // ----------------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------
  // Submit ---------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------
  
  const handleClickSubmit = async () => {
    const subscription = getSubscription()
    
    if (!subscription) {
      return
    }

    setIsLoading(true)
    
    const custom_admin_ui = selected_services.find(item => item.service_type === 'frontend' && item.service === 'custom_admin_ui')
    const hasAdminUi = custom_admin_ui ? true : false

    const custom_client_ui = selected_services.find(item => item.service_type === 'frontend' && item.service === 'custom_client_ui')
    const hasCustomerUi = custom_client_ui ? true : false

    const openstack_service = selected_services.find(item => item.service_type === 'backend')
    const backup_service = selected_services.find(item => item.service_type === 'backup')
    const ui_service = custom_admin_ui || custom_client_ui
    const hasBackend = ((openstack_service && ui_service) || backup_service) ? true : false
    
    const billing_service = selected_services.find(item => item.service_type === 'billing')
    const hasBilling = billing_service ? true : false
    
    const end_time = subscription.duration === 'month' ? calculateDateTime('months', 'plus', 1) : calculateDateTime('years', 'plus', 1)

    let services = []

    selected_services.forEach(item => {
      services.push({
        priority: item.priority,
        service_group: item.service_group,
        service_type: item.service_type,
        service: item.service,
        title: item.title,
      })
    })

    const subscriptionPostData = {
      client_id: account_id,
      package_name: subscription.package_name,
      package_group: subscription.group,
      duration: subscription.duration,
      fee: totalFee,
      currency: subscription.currency,
      start_time: getFormattedDateTime(),
      end_time,
      renewable: subscription_renewable,
      payment_method: selectedPaymentMethod,
      title: subscription.title,
      services,
    }

    const clientSubscriptionResponse = await clientSubscriptionRequest(subscriptionPostData)

    if (!clientSubscriptionResponse) {
      setIsLoading(false)
      return
    }

    if (hasBackend) {
      for (let index = 0; index < openstack_configs.length; index++) {
        const item = openstack_configs[index]
        
        const backend_service_title = getServiceTitle(item.service, subscription)

        let backendPostData = {
          service_group: subscription.group,
          service_type: 'backend',
          service: item.service,
          title: backend_service_title,
          config_params: {
            service_domain: item.service_domain,
            api_version: item.api_version,
            is_identity_provider: item.is_identity_provider,
            allowed_roles: getAllowedRoles(item.service, supportedServiceConfigs),
            visible_to_service: item.visible_to_service,
          },
        }

        if (item.is_identity_provider) {
          backendPostData.config_params.default_region = item.default_region
          backendPostData.app_credential_id = item.app_credential_id
          backendPostData.app_credential_secret = appCredentialSecret
        } else {
          backendPostData.config_params.region_name = item.region_name
        }
        
        await clientServiceConfigsRequest(backendPostData)
      }
    }

    if (hasAdminUi) {
      const admin_ui_service_title = getServiceTitle('custom_admin_ui', subscription)
      let admin_ui_logo_url = ''

      if (admin_ui_configs.logo) {
        const admin_ui_logo_filename = await uploadFileRequest(admin_ui_configs.logo, 'getUploadUiLogo')
        if (admin_ui_logo_filename) {
          admin_ui_logo_url = admin_ui_logo_filename
        }
      }

      let admin_ui_domains = []

      admin_ui_configs.service_domain.forEach(item => {
        admin_ui_domains.push(item.subdomain_prefix ? item.subdomain_prefix + '.' + item.domain_name : item.domain_name)
      })

      const adminUiPostData = {
        service_group: subscription.group,
        service_type: 'frontend',
        service: 'custom_admin_ui',
        title: admin_ui_service_title,
        config_params: {
          service_domain: admin_ui_domains,
          primary_color: admin_ui_configs.primary_color,
          secondary_color: admin_ui_configs.secondary_color,
          logo: admin_ui_logo_url,
          captcha_config: admin_ui_configs.captcha_config,
        },
      }

      await clientServiceConfigsRequest(adminUiPostData)

      let adminUiCertificatePostData = {
        ui_type: 'admin',
        generate_certificate: admin_ui_configs.certificate_generate,
      }

      if (admin_ui_configs.certificate_generate) {
        adminUiCertificatePostData.domains = getDomainList(admin_ui_configs.service_domain)
      } else {
        let adminUiCertificateList = []

        admin_ui_configs.certificates.forEach(item => {
          adminUiCertificateList.push({
            domains: getDomainListFromCertificate(admin_ui_configs.service_domain, item.domain),
            expiration_date: getIsoDateTime(item.expire_date),
            certificate: item.certificate,
            private_key: item.private_key,
            chain: item.full_chain,
          })
        })

        adminUiCertificatePostData.certificates = adminUiCertificateList
      }

      await clientCertificateConfigsRequest(adminUiCertificatePostData)
    }

    if (hasCustomerUi) {
      const customer_ui_service_title = getServiceTitle('custom_client_ui', subscription)
      let customer_ui_logo_url = ''
      let customer_ui_background_image = ''
      let customer_ui_terms_of_use_file = ''
      let customer_ui_privacy_policy_file = ''

      if (customer_ui_configs.logo) {
        const customer_ui_logo_filename = await uploadFileRequest(customer_ui_configs.logo, 'getUploadUiLogo')
        if (customer_ui_logo_filename) {
          customer_ui_logo_url = customer_ui_logo_filename
        }
      }

      if (customer_ui_configs.promo_config.background_image) {
        const bg_filename = await uploadFileRequest(customer_ui_configs.promo_config.background_image, 'getUploadUiBg')
        if (bg_filename) {
          customer_ui_background_image = bg_filename
        }
      }

      const terms_of_use_file = getDocumentFile('Terms of Use', customer_ui_configs.signup_config.documents)
      if (terms_of_use_file) {
        const terms_of_use_filename = await uploadFileRequest(terms_of_use_file, 'getUploadUiDoc')
        if (terms_of_use_filename) {
          customer_ui_terms_of_use_file = terms_of_use_filename
        }
      }

      const privacy_policy_file = getDocumentFile('Privacy Policy', customer_ui_configs.signup_config.documents)
      if (privacy_policy_file) {
        const privacy_policy_filename = await uploadFileRequest(privacy_policy_file, 'getUploadUiDoc')
        if (privacy_policy_filename) {
          customer_ui_privacy_policy_file = privacy_policy_filename
        }
      }

      let customer_ui_domains = []

      customer_ui_configs.service_domain.forEach(item => {
        customer_ui_domains.push(item.subdomain_prefix ? item.subdomain_prefix + '.' + item.domain_name : item.domain_name)
      })

      const customer_ui_promo_config = {
        ...customer_ui_configs.promo_config,
        background_image: customer_ui_background_image,
      }

      let customer_ui_documents = []

      customer_ui_configs.signup_config.documents.forEach(item => {
        if (item.label === 'Terms of Use' && typeof item.value !== 'string') {
          customer_ui_documents.push({ ...item, value: customer_ui_terms_of_use_file })
        } else if (item.label === 'Privacy Policy' && typeof item.value !== 'string') {
          customer_ui_documents.push({ ...item, value: customer_ui_privacy_policy_file })
        } else {
          customer_ui_documents.push(item)
        }
      })

      const customer_ui_signup_config = {
        ...customer_ui_configs.signup_config,
        documents: customer_ui_documents,
      }

      const customerUiPostData = {
        service_group: subscription.group,
        service_type: 'frontend',
        service: 'custom_client_ui',
        title: customer_ui_service_title,
        config_params: {
          service_domain: customer_ui_domains,
          primary_color: customer_ui_configs.primary_color,
          secondary_color: customer_ui_configs.secondary_color,
          logo: customer_ui_logo_url,
          openstack_domain: customer_ui_configs.openstack_domain,
          openstack_role: customer_ui_configs.openstack_role,
          contact_us: customer_ui_configs.contact_us,
          promo_config: customer_ui_promo_config,
          captcha_config: customer_ui_configs.captcha_config,
          signup_config: customer_ui_signup_config,
        },
      }

      await clientServiceConfigsRequest(customerUiPostData)

      let customerUiCertificatePostData = {
        ui_type: 'client',
        generate_certificate: customer_ui_configs.certificate_generate,
      }

      if (customer_ui_configs.certificate_generate) {
        customerUiCertificatePostData.domains = getDomainList(customer_ui_configs.service_domain)
      } else {
        let customerUiCertificateList = []

        customer_ui_configs.certificates.forEach(item => {
          customerUiCertificateList.push({
            domains: getDomainListFromCertificate(customer_ui_configs.service_domain, item.domain),
            expiration_date: getIsoDateTime(item.expire_date),
            certificate: item.certificate,
            private_key: item.private_key,
            chain: item.full_chain,
          })
        })

        customerUiCertificatePostData.certificates = customerUiCertificateList
      }

      await clientCertificateConfigsRequest(customerUiCertificatePostData)
    }
    
    if (hasBilling) {
      const billing_service_title = getServiceTitle('openstack_billing', subscription)

      const billingPostData = {
        service_group: subscription.group,
        service_type: 'billing',
        service: 'openstack_billing',
        title: billing_service_title,
        config_params: billing_permissions,
      }

      await clientServiceConfigsRequest(billingPostData)

      let payment_methods = []

      billing_configs.payment_methods.forEach(item => {
        const payment_method_credentials = paymentMethodIds && paymentMethodIds[item.name] && paymentMethodIds[item.name].keyValue && paymentMethodIds[item.name].idValue ? { [paymentMethodIds[item.name].keyValue]: paymentMethodIds[item.name].idValue } : null

        payment_methods.push({
          ...item,
          credentials: payment_method_credentials,
        })
      })

      let billingAccountConfigsPostData = {
        multiregion_support: true,
        currency: {
          name: billing_configs.currency,
          code: getCurrencyCode(billing_configs.currency),
        },
        payment_methods,
        status: billing_configs.status,
        default_billing_type: billing_configs.default_billing_type,
        postpaid_invoice: billing_configs.postpaid_invoice ? billing_configs.postpaid_invoice : 'auto',
        postpaid_invoice_pay_period: billing_configs.postpaid_invoice_pay_period ? parseInt(billing_configs.postpaid_invoice_pay_period) : 0,
        use_vat_tax: billing_configs.use_vat_tax,
        price_includes_vat: billing_configs.price_includes_vat,
        vat: parseInt(billing_configs.vat),
        allow_public_price_calculator: billing_configs.allow_public_price_calculator,
      }
      
      const billingAcountConfigsId = await billingAccountConfigsIdRequest()
      
      if (billingAcountConfigsId) {
        billingAccountConfigsPostData.status = 'active'
        await billingAccountConfigsUpdateRequest(billingAccountConfigsPostData)
      } else {
        billingAccountConfigsPostData.account_id = account_id
        await billingAccountConfigsRequest(billingAccountConfigsPostData)
      }      

      for (let index = 0; index < openstack_configs.length; index++) {
        const item = openstack_configs[index]

        if (item.enable_billing) {
          let billingAccountServicePostData = {
            account_id: account_id,
            is_identity_provider: item.is_identity_provider,
            service_name: item.service,
            service_domain: item.service_domain,
            service_version: item.api_version,
            region_name: item.is_identity_provider ? item.default_region : item.region_name,
            resource_suspend_period: item.resource_suspend_period,
            resource_delete_period: item.resource_delete_period,
            resource_suspend_policy: item.resource_suspend_policy,
            resource_delete_policy: item.resource_delete_policy,
          }
  
          if (item.is_identity_provider) {
            billingAccountServicePostData.credentials = {
              app_auth_id: item.app_credential_id,
              app_auth_secret: appCredentialSecret,
            }
          }
          
          await billingAccountServicesRequest(billingAccountServicePostData)
        }        
      }
    }

    if (backup_service) {
      const service_config_object = supportedServiceConfigs.find(con => con.service === backup_service.service)

      let backupPostData = {
        service_group: backup_service.service_group,
        service_type: backup_service.service_type,
        service: backup_service.service,
        title: backup_service.title,
        config_params: service_config_object && service_config_object.config_params ? service_config_object.config_params : {},
      }

      await clientServiceConfigsRequest(backupPostData)
    }

    setIsLoading(false)
    dispatch(subscriptionAddReset())
    navigate('/', { state: { fetch_subscriptions: true } })
  }
  
  useEffect(() => {
    const fetchProfileData = async () => {
      const url = constants.core_service_domain + '/' + clientAccountsURLs.getProfile      
      const requestData = { url, method: 'GET' }    
      const response = await request(requestData)
      
      if (response && response.status_code && response.status_code < 400 && response.data && response.data.account_status && response.data.account_status === 'active') {
        dispatch(setAccountBalance({ account_balance: response.data.account_balance }))
      }
    }

    fetchProfileData()
  }, [dispatch])

  useEffect(() => {
    if (pending_payment) {
      let count = 0
      let intervalID = setInterval( () => {
        count++

        const url = constants.core_service_domain + '/' + clientAccountsURLs.getProfile
        const request_data = { url, method: 'GET' }

        request(request_data).then(response => {
          if (response && response.status_code && response.status_code < 400 && response.data && response.data.account_balance && response.data.account_balance !== account_balance) {
            dispatch(setAccountBalance({ account_balance: response.data.account_balance }))
            dispatch(setPendingPayment({ pending_payment: false }))
            clearInterval(intervalID)
          } else if (count === 10) {
            dispatch(setPendingPayment({ pending_payment: false }))
            clearInterval(intervalID)
          }
        })
      }, 60000)
    }    
  }, [pending_payment, dispatch, account_balance])

  useEffect(() => {
    if (account_balance >= totalFee && selectedPaymentMethod) {
      setAllowSubmit(true)
      dispatch(setPendingPayment({ pending_payment: false }))
    } else {
      setAllowSubmit(false)
    }
  }, [account_balance, totalFee, selectedPaymentMethod, dispatch])

  useEffect(() => {
    const balanceRefillRequest = async (postData) => {
      const url = constants.core_service_domain + '/' + clientAccountsURLs.getBalanceRefill      
      const request_data = { url, method: 'POST', data: postData }
      await request(request_data)
      setRefillBalanceData(null)
    }

    if (refillBalanceData) {
      balanceRefillRequest(refillBalanceData)
    }
  }, [refillBalanceData])

  useEffect(() => {
    let slider_data = []

    slider_data.push(
      <SubmitPaymentMethodBalance
        selectedPaymentMethod={selectedPaymentMethod}
        handleSelectPaymentMethod={handleSelectPaymentMethod}
      />
    )

    // slider_data.push(
    //   <SubmitPaymentMethodSubscribe
    //     selectedPaymentMethod={selectedPaymentMethod}
    //     handleSelectPaymentMethod={handleSelectPaymentMethod}
    //   />
    // )

    setPaymentMethodsSliderData(slider_data)

    let slides_to_show = 2

    if (width < constants.mdBreakpoint) {
      slides_to_show = 1
    }

    setPaymentMethodsSliderSlidesToShow(slides_to_show)
  }, [width, selectedPaymentMethod])
  
  return (
    <>
      { isLoading && <CustomBackdrop open={isLoading} /> }

      <Box sx={labelStyle}>{defaultTexts.selectPaymentMethodAndSubmit}</Box>

      <Box sx={paymentMethodsWrapperStyle}>
        {
          paymentMethodsSliderData.length > paymentMethodsSliderSlidesToShow ?
          <CustomSlickSlider
            slides={paymentMethodsSliderData}
            arrows={paymentMethodsSliderArrows}
            slidesToShow={paymentMethodsSliderSlidesToShow}
            draggable={true}     
            centerMode={width < constants.mdBreakpoint ? true : false}
            sx={{ margin: width >= constants.mdBreakpoint ? '0px 50px' : '0px' }}
          /> :
          <Grid container>
            <Grid size={{ xs: 12, lg: 6 }}>
              <SubmitPaymentMethodBalance
                selectedPaymentMethod={selectedPaymentMethod}
                handleSelectPaymentMethod={handleSelectPaymentMethod}
              />
            </Grid>
            {/* <Grid size={{ xs: 12, lg: 6 }}>
              <SubmitPaymentMethodSubscribe
                selectedPaymentMethod={selectedPaymentMethod}
                handleSelectPaymentMethod={handleSelectPaymentMethod}
              />
            </Grid> */}
          </Grid>
        }
      </Box>

      {
        (selectedPaymentMethod === 'balance' && totalFee > account_balance) &&
        <>
          <Box sx={labelStyle}>{defaultTexts.emptyBalanceNotificationText}</Box>

          <Grid container spacing={2}>
            <Grid size={{ lg: 5, xl: 3 }}>
              <CustomTextField 
                variant='outlined'
                label={defaultTexts.amount}
                currentValue={amount}
                setCurrentValue={handleChangeAmount}
                sx={formControlStyle}
                outlinedInputSx={outlinedInputStyle}
              />
            </Grid>
            <Grid size={{ lg: 1 }}>
              <Box sx={currencyLabelStyle}>USD</Box>
            </Grid>
            <Grid size={{ md: 6, lg: 4, xl: 2 }}>
              {/* <Button
                variant='text'
                disableRipple
                disableElevation
                sx={creditCardButtonStyle}
              >
                {defaultTexts.creditCard}
              </Button> */}
              {
                (amount && parseFloat(amount) > 0) &&
                <PaypalWrapper
                  amount={amount}
                  paypalOptions={paypalOptions}
                  onSuccess={onSuccessfulPayment}
                  onFail={onFailedPayment}
                />
              }              
            </Grid>
          </Grid>
        </>
      }

      {
        selectedPaymentMethod === 'paypal' &&
        <Grid container spacing={2} sx={{ marginTop: '40px' }}>
          <Grid size={{ lg: 5, xl: 3 }}>
            <Box sx={subscribeHelperTextStyle}>{parse(defaultTexts.subscribeWithPaypalHelperText)}</Box>
          </Grid>
          <Grid size={{ lg: 1 }}></Grid>
          <Grid size={{ md: 6, lg: 4, xl: 2 }}>
            <PaypalWrapper
              amount={amount}
              paypalOptions={paypalOptions}
              onSuccess={onSuccessfulPayment}
              onFail={onFailedPayment}
            />
          </Grid>
        </Grid>
      }

      <Stack
        direction='row'
        justifyContent='space-between'
        sx={{ marginTop: '20px' }}
      >
        <Button
          variant='text'
          disableRipple
          disableElevation
          sx={nextButtonStyle}
          onClick={handleClickPrev}
        >
          {defaultTexts.back}
        </Button>

        {
          (allowSubmit && !pending_payment) &&
          <Button
            variant='text'
            disableRipple
            disableElevation
            disabled={false}
            onClick={handleClickSubmit}
            sx={submitButtonStyle}
          >
            {defaultTexts.submit}
          </Button>
        }

        { pending_payment && <CircularProgress /> }
      </Stack>

      <CustomDialog
        open={payResultDialogOpen}
        onClose={handlePayResultDialogClose}
        dialogTitle={{
          title: defaultTexts.payment, 
        }}
        dialogBody={{
          text: payResultText, 
        }}
			/>  
    </>
  )
}

export default SubmitWrapper